/**
 * StatusBoard — multi-signal sync-health widget for the Overview tab.
 *
 * Polls GET /api/v1/admin/status-board every 60 s and renders a compact
 * grid of green / yellow / red status dots. Clicking a row deep-links to
 * the relevant tab (passed in via onNav).
 *
 * Strictly uses the mockup CSS tokens (var(--ok)/var(--warn)/var(--bad)).
 */
import { useEffect, useState } from 'react';
import { Activity, ChevronRight, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TONE_MAP = {
  ok: { dot: 'var(--ok, #10b981)', soft: 'rgba(16, 185, 129, 0.16)', fg: '#10b981' },
  warn: { dot: 'var(--warn, #f59e0b)', soft: 'rgba(245, 158, 11, 0.18)', fg: '#f59e0b' },
  down: { dot: 'var(--bad, #ef4444)', soft: 'rgba(239, 68, 68, 0.18)', fg: '#ef4444' },
};

function StatusDot({ status }) {
  const tone = TONE_MAP[status] || TONE_MAP.warn;
  return (
    <span
      aria-label={`status ${status}`}
      style={{
        width: 10,
        height: 10,
        borderRadius: 99,
        background: tone.dot,
        flex: '0 0 10px',
        boxShadow: `0 0 0 3px ${tone.soft}`,
      }}
    />
  );
}

export default function StatusBoard({ onNav, sessionToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!sessionToken) return;
    let alive = true;
    const run = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/v1/admin/status-board`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (alive) {
          setData(json);
          setErr('');
        }
      } catch (e) {
        if (alive) setErr(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    const id = setInterval(run, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [sessionToken, refreshTick]);

  const overall = data?.overall || 'ok';
  const checks = data?.checks || [];
  const overallTone = TONE_MAP[overall] || TONE_MAP.ok;

  return (
    <div
      data-testid="status-board"
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        padding: 18,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 28, height: 28, borderRadius: 8,
            background: 'var(--info-soft)', color: 'var(--accent-deep)',
            display: 'grid', placeItems: 'center',
          }}>
            <Activity size={15} />
          </span>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>
            Status board
          </h2>
          {!loading && !err && (
            <span
              data-testid="status-board-overall"
              data-status={overall}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '3px 10px', borderRadius: 99,
                background: overallTone.soft,
                color: overallTone.fg,
                fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase',
                letterSpacing: 0.3,
              }}
            >
              <StatusDot status={overall} />
              {overall === 'ok' ? 'Healthy' : overall === 'warn' ? 'Watch' : 'Action needed'}
            </span>
          )}
        </div>
        <button
          data-testid="status-board-refresh"
          onClick={() => setRefreshTick((t) => t + 1)}
          aria-label="Refresh status board"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 8px', borderRadius: 6,
            background: 'transparent', border: '1px solid var(--border)',
            color: 'var(--muted)', cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      {loading && (
        <div data-testid="status-board-loading" style={{ display: 'grid', gap: 8 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="lb2-shimmer" style={{ height: 36, borderRadius: 'var(--r-sm)' }} />
          ))}
        </div>
      )}

      {err && !loading && (
        <div data-testid="status-board-error" style={{
          padding: 12, borderRadius: 'var(--r-sm)',
          background: 'var(--bad-soft, rgba(239,68,68,0.12))',
          color: 'var(--bad, #ef4444)',
          fontWeight: 600, fontSize: 13,
        }}>
          Couldn&apos;t load status ({err}).{' '}
          <button data-testid="status-board-button-1"
            onClick={() => setRefreshTick((t) => t + 1)}
            style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !err && checks.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          {checks.map((c) => {
            const tone = TONE_MAP[c.status] || TONE_MAP.warn;
            const interactive = !!c.tab && typeof onNav === 'function';
            const Wrapper = interactive ? 'button' : 'div';
            return (
              <Wrapper
                key={c.key}
                data-testid={`status-check-${c.key}`}
                data-status={c.status}
                onClick={interactive ? () => onNav(c.tab) : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px', borderRadius: 'var(--r-sm)',
                  border: '1px solid var(--border)',
                  background: 'var(--surface-2)',
                  textAlign: 'left', width: '100%',
                  cursor: interactive ? 'pointer' : 'default',
                  font: 'inherit', color: 'var(--text)',
                }}
              >
                <StatusDot status={c.status} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{c.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>
                    {c.detail}
                  </div>
                </span>
                {c.value != null && (
                  <span style={{
                    fontSize: 12, fontWeight: 800, color: tone.fg,
                    background: tone.soft,
                    padding: '2px 8px', borderRadius: 99,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {c.value}
                  </span>
                )}
                {interactive && <ChevronRight size={14} style={{ color: 'var(--muted)' }} />}
              </Wrapper>
            );
          })}
        </div>
      )}
    </div>
  );
}
